import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	IconButton,
	Checkbox,
	Button,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileUploadModal from "./FileUploadModal";
import moment from "moment";
import FileItemMenu from "./FileItemMenu";
import {
	fetchFolderContents,
	deleteFiles,
	selectFilteredFolderContents,
} from "../../../store/DocumentsSlice";

const FolderView = ({
	folderPath,
	onBack,
	handleFileUpload,
	folders,
	downloadFile,
}) => {
	const dispatch = useDispatch();
	const { loading, searchQuery } = useSelector((state) => state.documents);
	// Use the filtered folder contents based on search query
	const files = useSelector(selectFilteredFolderContents);

	const [selected, setSelected] = useState([]);
	const [openUploadModal, setOpenUploadModal] = useState(false);
	const isAllSelected = files.length > 0 && selected.length === files.length;

	const handleSelectAll = () => {
		setSelected(isAllSelected ? [] : files.map((_, index) => index));
	};

	const handleSelect = (index) => {
		setSelected((prevSelected) =>
			prevSelected.includes(index)
				? prevSelected.filter((i) => i !== index)
				: [...prevSelected, index]
		);
	};

	const handleDelete = async () => {
		if (selected.length === 0) return;

		try {
			// Get the IDs of selected files
			const fileIds = selected.map((index) => files[index].id);

			// Make API call to delete the files
			await dispatch(deleteFiles(fileIds)).unwrap();

			// Clear selection after successful deletion
			setSelected([]);
		} catch (error) {
			console.error("Error deleting files:", error);
		}
	};

	const handleMultiDownload = async () => {
		if (selected.length === 0) return;
		try {
			// Get the IDs of selected files
			const fileIds = selected.map((index) => files[index].id);
			downloadFile(fileIds);
		} catch (error) {
			console.error("Error downloading files:", error);
		}
	};

	const handleSingleFileDelete = async (fileId) => {
		try {
			await dispatch(deleteFiles([fileId])).unwrap();

			// Also remove from selected if it was selected
			const indexInSelected = selected.find(
				(index) => files[index].id === fileId
			);
			if (indexInSelected !== undefined) {
				setSelected(
					selected.filter((index) => files[index].id !== fileId)
				);
			}
		} catch (error) {
			console.error("Error deleting file:", error);
		}
	};

	useEffect(() => {
		if (folderPath) {
			dispatch(fetchFolderContents(folderPath));
		}
	}, [folderPath, dispatch]);

	// Clear selection when folder changes
	useEffect(() => {
		setSelected([]);
	}, [folderPath]);

	return (
		<Box>
			{/* <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ ml: 2 }}>
          Back
        </Button>
      </Box> */}

			{loading ? (
				<Typography>Loading...</Typography>
			) : (
				<>
					<Box sx={{ padding: "20px" }}>
						{/* Header Section */}
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ marginBottom: 0, marginTop: "1rem" }}>
							<Box display='flex' gap='0.5rem'>
								<Typography
									variant='h6'
									fontWeight='800'
									onClick={onBack}
									sx={{ cursor: "pointer" }}>
									{folderPath?.split("/")?.[0]}
								</Typography>
								<Typography variant='h6' fontWeight='800'>
									{` >`}
								</Typography>
								<Typography variant='h6' fontWeight='800'>
									{folderPath?.split("/")?.[1]}
								</Typography>
							</Box>
							<Button
								variant='contained'
								sx={{
									background: "blue",
									borderRadius: "8px",
									padding: "8px 30px",
									fontWeight: "bold",
								}}
								startIcon={<FileUploadOutlinedIcon />}
								onClick={() => setOpenUploadModal(true)}>
								Upload
							</Button>
						</Box>

						{searchQuery ? (
							<Typography variant='body2' mb={2}>
								Search results for:{" "}
								<strong>"{searchQuery}"</strong> ({files.length}{" "}
								items)
							</Typography>
						) : (
							<Typography variant='body2' mb={8}>
								{files.length} Items
							</Typography>
						)}

						{files.length > 0 ? (
							<TableContainer
								component={Paper}
								sx={{ marginTop: 2 }}>
								<Table>
									<TableHead>
										<TableRow
											sx={{ backgroundColor: "#D6F0FF" }}>
											<TableCell
												sx={{
													width: "90px",
												}}>
												<Checkbox
													checked={isAllSelected}
													onChange={handleSelectAll}
													sx={{
														color: "gray",
														"&.Mui-checked": {
															color: "blue",
														},
													}}
												/>
											</TableCell>
											{selected.length > 0 ? (
												<TableCell colSpan={5}>
													<Box
														display='flex'
														justifyContent='space-between'
														alignItems='center'>
														<Typography
															variant='body1'
															sx={{
																color: "blue",
															}}>
															{selected.length}{" "}
															Items Selected
														</Typography>
														<Box>
															<IconButton
																sx={{
																	color: "blue",
																}}>
																<FileDownloadRoundedIcon
																	onClick={
																		handleMultiDownload
																	}
																/>
															</IconButton>
															<IconButton
																sx={{
																	color: "blue",
																}}>
																<DeleteForeverRoundedIcon
																	onClick={
																		handleDelete
																	}
																/>
															</IconButton>
														</Box>
													</Box>
												</TableCell>
											) : (
												<>
													<TableCell>
														<strong>
															File Name
														</strong>
													</TableCell>
													<TableCell>
														<strong>Size</strong>
													</TableCell>
													<TableCell>
														<strong>Type</strong>
													</TableCell>
													<TableCell>
														<strong>
															Date Added
														</strong>
													</TableCell>
													<TableCell></TableCell>
												</>
											)}
										</TableRow>
									</TableHead>

									{/* Table Body */}
									<TableBody>
										{files.map((doc, index) => (
											<TableRow key={index}>
												<TableCell>
													<Checkbox
														checked={selected.includes(
															index
														)}
														onChange={() =>
															handleSelect(index)
														}
													/>
												</TableCell>
												<TableCell>
													{doc.name?.split(".")?.[0]}
												</TableCell>
												<TableCell>
													{Math.floor(doc.size)} Kb
												</TableCell>
												<TableCell>
													{doc?.ext?.split(".")?.[1]}
												</TableCell>
												<TableCell>
													{moment(
														doc.createdAt
													).format("DD MMM YYYY")}
												</TableCell>
												<TableCell>
													<FileItemMenu
														onDownload={() => {
															downloadFile(
																doc.id
															);
														}}
														onDelete={() =>
															handleSingleFileDelete(
																doc.id
															)
														}
													/>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						) : (
							<Typography
								variant='body2'
								color='text.secondary'
								ml={2.5}>
								{searchQuery
									? `No files matching "${searchQuery}" in this folder`
									: "No files in this folder"}
							</Typography>
						)}
					</Box>
					<FileUploadModal
						open={openUploadModal}
						onClose={() => setOpenUploadModal(false)}
						folders={folders}
						onUpload={handleFileUpload}
						currentFolderPath={folderPath}
					/>
				</>
			)}
		</Box>
	);
};

export default FolderView;
